import { LitElement, html } from 'lit';

import { REFRESH_EVENT } from '../../lib/poll/index.js';
import { api } from '../api/index.js';
import { enrollmentTokenCard } from '../components/enrollment-token.js';
import { confirmAction } from '../components/plane-confirm.js';
import { connectionBadge, serverStatusBadge } from '../components/plane-badge.js';
import { loadState, staleWarning } from '../components/load-state.js';
import { when } from '../components/time.js';
import { idFollowsName } from '../format.js';
import { poll } from '../poll.js';
import { formStyles } from '../../styles/shared/forms.js';
import { pageStyles } from '../../styles/shared/page.js';
import { TableLabels } from '../table-labels.js';
import { tableStyles } from '../../styles/shared/tables.js';
import { enrollmentTokenStyles } from '../../styles/components/enrollment-token.js';
import { serversPageStyles } from '../../styles/pages/servers-page.js';

/**
 * The generation pair of a server: in sync, or waiting for the clawlet to acknowledge, or held
 * because its clawlet is ahead of the plane.
 */
export const generation = (server) =>
  server.aheadGeneration != null
    ? html`<plane-badge tone="warning" title="The clawlet has generation ${server.aheadGeneration}, ahead of the plane: nothing is sent to it until it is taken over">held</plane-badge>`
    : server.ackedGeneration >= server.desiredGeneration
    ? html`<span title="Acknowledged by the clawlet">${server.desiredGeneration} ✓</span>`
    : html`<span title="Desired / acknowledged by the clawlet">${server.desiredGeneration} <span class="muted">/ ${server.ackedGeneration}</span></span>`;

/** Where an enrollment request came from, compared with where the clawlet is expected to come from. */
const NETWORK = {
  expected: { label: 'Expected network', tone: 'ok', hint: 'From the networks of the installation or of the server it asks for' },
  local: { label: 'This machine', tone: 'muted', hint: 'From the machine the plane runs on: any process of it looks the same' },
  unknown: { label: 'Unknown network', tone: 'warning', hint: 'Not from a network this server or the installation is expected to come from' },
};

export const networkBadge = (code) => {
  const network = NETWORK[code] ?? NETWORK.unknown;
  return html`<plane-badge tone=${network.tone} title=${network.hint}>${network.label}</plane-badge>`;
};

/** The start of a key fingerprint, as people compare it with the log of the clawlet; the whole on hover. */
const shortFingerprint = (fingerprint) => html`<code title=${fingerprint}>${fingerprint.slice(0, 16)}…</code>`;

/**
 * Operator view: the clawlets waiting for an operator to approve their enrollment request, every server and the state
 * of its clawlet connection; a new server and its enrollment token.
 */
class ServersPage extends LitElement {
  static properties = {
    busy: { state: true },
    actionError: { state: true },
    issued: { state: true },
  };

  static styles = [
    pageStyles,
    tableStyles,
    formStyles,
    enrollmentTokenStyles,
    serversPageStyles,
  ];

  constructor() {
    super();
    new TableLabels(this);
    this.busy = false;
    this.actionError = null;
    this.issued = null;
    this.poll = poll(this, async () => {
      const [servers, requests] = await Promise.all([api.servers(), api.enrollmentRequests()]);
      return { servers, requests };
    });
  }

  /** Approves a request for the server of its form: created if it does not exist. */
  async approve(event, request) {
    event.preventDefault();
    const form = event.target;
    const serverId = form.elements.serverId.value.trim();
    const existing = this.poll.value.servers.find((server) => server.id === serverId);
    if (
      existing &&
      existing.connection !== 'never' &&
      !(await confirmAction({
        heading: `Give ${serverId} a new key?`,
        message: `${serverId} is already enrolled. Approving revokes its current key: its connection closes, the clawlet of this request takes its place, and the secrets sealed to the old key must be set again.`,
        confirmLabel: 'Approve',
        tone: 'danger',
      }))
    ) {
      return;
    }
    this.busy = true;
    this.actionError = null;
    try {
      await api.approveEnrollmentRequest(request.id, {
        serverId,
        name: form.elements.name.value.trim() || null,
        rememberAddress: form.elements.remember.checked,
      });
    } catch (error) {
      this.actionError = error.message;
    } finally {
      this.busy = false;
      // this page, and the count of the requests in the header
      document.body.dispatchEvent(new CustomEvent(REFRESH_EVENT));
    }
  }

  async deny(request) {
    if (
      !(await confirmAction({
        heading: 'Deny this enrollment request?',
        message: `The key ${request.fingerprint.slice(0, 16)}… will never be approved. The clawlet asks again later, with a new key: deny that too if it is not one of yours.`,
        confirmLabel: 'Deny',
        tone: 'danger',
      }))
    ) {
      return;
    }
    this.busy = true;
    this.actionError = null;
    try {
      await api.denyEnrollmentRequest(request.id);
    } catch (error) {
      this.actionError = error.message;
    } finally {
      this.busy = false;
      document.body.dispatchEvent(new CustomEvent(REFRESH_EVENT));
    }
  }

  renderRequest(request) {
    const server = request.server;
    const moved = server && request.serverLastAddress && request.serverLastAddress !== request.sourceAddress;
    return html`
      <div class="card request">
        <dl class="facts">
          <dt>Asks for</dt>
          <dd>${request.requestedServerId ? html`<code>${request.requestedServerId}</code>` : html`<span class="muted">no server id</span>`}</dd>
          <dt>Host</dt>
          <dd>${request.hostname ? html`<code>${request.hostname}</code>` : html`<span class="muted">not given</span>`}</dd>
          <dt>From</dt>
          <dd><code>${request.sourceAddress}</code> ${networkBadge(request.network)}</dd>
          <dt>Key</dt>
          <dd>${shortFingerprint(request.fingerprint)}</dd>
          <dt>Asked</dt>
          <dd>${when(request.createdAt)}, last ${when(request.lastSeenAt)}</dd>
        </dl>
        ${server
          ? html`<p class="muted">
              <a href="/servers/${server.id}"><code>${server.id}</code></a> exists (${server.name}${server.status === 'revoked' ? ', revoked' : ''}): approving it gives it a new key and revokes the
              current one.${moved ? html` Its last connection came from <code>${request.serverLastAddress}</code>, not from this address.` : null}
            </p>`
          : null}
        <form class="inline" @submit=${(event) => this.approve(event, request)}>
          <input name="serverId" type="text" required pattern="[A-Za-z0-9_\-]{1,64}" maxlength="64" .value=${request.requestedServerId ?? ''} placeholder="Server id" aria-label="Server id" />
          <input name="name" type="text" maxlength="200" placeholder="Name, if the server is new" aria-label="Server name" />
          <label class="check"><input name="remember" type="checkbox" ?checked=${request.network !== 'local'} /><span>Expect this server from <code>${request.sourceAddress}</code></span></label>
          <button class="primary" ?disabled=${this.busy}>Approve</button>
          <button type="button" class="danger" ?disabled=${this.busy} @click=${() => this.deny(request)}>Deny</button>
        </form>
      </div>
    `;
  }

  renderRequests(requests) {
    if (requests.length === 0) {
      return null;
    }
    return html`
      <section>
        <h2>Clawlets waiting for approval</h2>
        <p class="muted">
          Deployed with the bootstrap key of this plane, they asked for a certificate and wait. Compare the key with the one in the log of the clawlet if in doubt. The id they ask for and their
          host are their own word: the certificate gets the server id you approve them for.
        </p>
        ${requests.map((request) => this.renderRequest(request))}
      </section>
    `;
  }

  async create(event) {
    event.preventDefault();
    const form = event.target;
    this.busy = true;
    this.actionError = null;
    try {
      this.issued = await api.createServer(form.elements.id.value.trim(), form.elements.name.value.trim());
      form.reset();
      idFollowsName.reset(form);
    } catch (error) {
      this.actionError = error.message;
    } finally {
      this.busy = false;
      this.poll.refresh();
    }
  }

  renderCreate() {
    return html`
      <section class="create">
        <h2>New server</h2>
        <p class="muted">
          A clawlet deployed with the bootstrap key needs no server here: approve its request above. Adding one issues an enrollment token instead, for a clawlet deployed with it. The id comes
          from the name unless you type one: it is the one its certificate carries, letters, digits, <code>-</code> and <code>_</code>, never changed afterwards.
        </p>
        <form class="inline" @submit=${(event) => this.create(event)}>
          <input name="name" type="text" required maxlength="200" placeholder="Name, e.g. EU 3" aria-label="Server name" @input=${(event) => idFollowsName.name(event, 64)} />
          <input name="id" type="text" required pattern="[A-Za-z0-9_\-]{1,64}" maxlength="64" placeholder="Id, e.g. eu-3" aria-label="Server id" @input=${(event) => idFollowsName.id(event)} />
          <button class="primary" ?disabled=${this.busy}>Add server</button>
        </form>
      </section>
    `;
  }

  render() {
    const head = html`
      <header class="page-head">
        <div>
          <div class="page-title">
            <h1>Servers</h1>
            <plane-help topic="servers">
              <p>Each server runs a clawlet, which connects to this plane and keeps the connection open. The plane never connects to the servers: they come to it, so a server needs no open port.</p>
              <p>A clawlet joins in one of two ways. Deployed with the bootstrap key of this plane, it asks for a certificate and waits here for your approval. Deployed with an enrollment token, the one <strong>Add server</strong> issues, it gets its certificate at once.</p>
            </plane-help>
          </div>
          <p class="lead">The servers whose clawlet connects to this plane.</p>
        </div>
      </header>
    `;
    const pending = loadState(this.poll, 'the servers');
    if (pending) {
      return html`${head}${pending}`;
    }
    const { servers, requests } = this.poll.value;
    const notices = html`
      ${this.actionError ? html`<div class="notice" data-tone="danger" role="alert"><strong>The action failed</strong>${this.actionError}</div>` : null}
      ${this.issued ? enrollmentTokenCard(this.issued, () => (this.issued = null)) : null}
    `;
    if (servers.length === 0) {
      return html`${head}${notices}${this.renderRequests(requests)}<p class="empty">No server yet.</p>${this.renderCreate()}`;
    }
    return html`
      ${head} ${staleWarning(this.poll)} ${notices} ${this.renderRequests(requests)}
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Server</th>
              <th>Connection</th>
              <th>Since</th>
              <th>Status</th>
              <th>Clawlet</th>
              <th class="num">Generation</th>
              <th class="num">Workspaces</th>
            </tr>
          </thead>
          <tbody>
            ${servers.map(
              (server) => html`
                <tr>
                  <td><a href="/servers/${server.id}"><code>${server.id}</code></a> <span class="muted">${server.name}</span></td>
                  <td>
                    ${connectionBadge(server.connection)}
                    ${server.lastError ? html`<plane-badge tone="warning" title=${server.lastError}>error</plane-badge>` : null}
                  </td>
                  <td>${server.connection === 'online' ? when(server.connectedSince) : when(server.disconnectedAt, 'never')}</td>
                  <td>${serverStatusBadge(server.status)}</td>
                  <td><code>${server.clawletVersion ?? '—'}</code></td>
                  <td class="num">${generation(server)}</td>
                  <td class="num">${server.instanceCount}</td>
                </tr>
              `,
            )}
          </tbody>
        </table>
      </div>
      ${this.renderCreate()}
    `;
  }
}

customElements.define('plane-servers-page', ServersPage);
